import { ref, computed, watch } from 'vue'

export function useOnlineLessons({
  api,
  snackbarStore,
  t,
  locale,
  nextTick,
  normalizeVideoSrc,
  selectedMode,
  authStore,
  onReturnToModeSelection
}) {
  const showLessons = ref(false)
  const lessonCategories = ref([])
  const selectedLessonCategory = ref(null)
  const currentLessons = ref([])
  const currentLesson = ref(null)
  const lessonsLoading = ref(false)
  const lessonVideoPlayer = ref(null)
  const lessonVideoSrc = ref(null)
  const lessonVideoErrorsSent = new Set()

  const loadLessonCategories = async () => {
    try {
      lessonsLoading.value = true
      const response = await api.get('/lessons/categories')
      lessonCategories.value = response.data.categories || []
    } catch (error) {
      snackbarStore.error(t('lessons.loadError'))
    } finally {
      lessonsLoading.value = false
    }
  }

  const selectLessonCategory = async (categoryId) => {
    try {
      lessonsLoading.value = true
      selectedLessonCategory.value = categoryId
      const response = await api.get(`/lessons/by-category/${categoryId}`)
      currentLessons.value = response.data || []
      currentLesson.value = currentLessons.value.length > 0 ? currentLessons.value[0] : null
    } catch (error) {
      snackbarStore.error(t('lessons.loadError'))
    } finally {
      lessonsLoading.value = false
    }
  }

  const selectLesson = (lesson) => {
    currentLesson.value = lesson
  }

  const getCurrentVideoUrl = computed(() => {
    if (!currentLesson.value) return null

    const lang = locale.value
    const videoUrlKey = `videoUrl${lang.charAt(0).toUpperCase() + lang.slice(1)}`
    const videoUrl = currentLesson.value[videoUrlKey]
      || currentLesson.value.videoUrlKz
      || currentLesson.value.videoUrlRu
      || currentLesson.value.videoUrlEn
      || null

    return normalizeVideoSrc(videoUrl)
  })

  const lessonFallbackVideoUrl = computed(() => {
    const primarySrc = getCurrentVideoUrl.value
    if (!primarySrc) return null

    const videoPathIndex = primarySrc.indexOf('/videos/')
    if (videoPathIndex === -1) return null

    const videoPath = primarySrc.slice(videoPathIndex)
    // Fallback origin must be configured via VITE_MEDIA_FALLBACK_ORIGIN.
    const explicitOrigin = import.meta.env.VITE_MEDIA_FALLBACK_ORIGIN
    if (!explicitOrigin) return null
    const fallbackBase = explicitOrigin.replace(/\/+$/, '')

    return `${fallbackBase}${videoPath}`
  })

  const resolvedLessonVideoUrl = computed(() => lessonVideoSrc.value || getCurrentVideoUrl.value)

  const onLessonVideoError = async (e) => {
    const video = lessonVideoPlayer.value || e?.target
    const mediaError = video?.error
    const code = mediaError?.code || 0
    // 1 = MEDIA_ERR_ABORTED: usually navigation or element teardown, not a load failure.
    if (code === 1) return

    const fallbackUrl = lessonFallbackVideoUrl.value
    const currentUrl = lessonVideoSrc.value || getCurrentVideoUrl.value

    if (fallbackUrl && currentUrl !== fallbackUrl) {
      lessonVideoSrc.value = fallbackUrl
      await nextTick()
      lessonVideoPlayer.value?.load?.()
      return
    }

    if (!authStore?.user?.id) return

    const videoSrc = video?.currentSrc || video?.src || resolvedLessonVideoUrl.value || getCurrentVideoUrl.value || 'unknown'
    const errorKey = `${currentLesson.value?.id || 'unknown'}:${videoSrc}:${code}`
    if (lessonVideoErrorsSent.has(errorKey)) return
    lessonVideoErrorsSent.add(errorKey)

    const codeMap = { 2: 'MEDIA_ERR_NETWORK', 3: 'MEDIA_ERR_DECODE', 4: 'MEDIA_ERR_SRC_NOT_SUPPORTED' }
    const codeName = codeMap[code] || 'VIDEO_ERROR'

    api.post('/errors', {
      userId: authStore.user?.id,
      userPhone: authStore.user?.phone,
      userName: authStore.user?.fio,
      errorType: 'VIDEO_ERROR',
      errorCode: `LESSON_${codeName}`,
      message: `Lesson video failed to load: ${videoSrc} (${codeName}: ${mediaError?.message || ''})`,
      url: window.location.href,
      method: 'GET',
      requestBody: {
        videoSrc,
        configuredSrc: resolvedLessonVideoUrl.value || getCurrentVideoUrl.value || null,
        fallbackSrc: lessonFallbackVideoUrl.value || null,
        currentSrc: video?.currentSrc || null,
        readyState: video?.readyState ?? null,
        networkState: video?.networkState ?? null,
        lessonId: currentLesson.value?.id || null,
        lessonTitleKz: currentLesson.value?.titleKz || null,
        lessonTitleRu: currentLesson.value?.titleRu || null,
        selectedCategoryId: selectedLessonCategory.value,
        locale: locale.value,
        videoUrlKz: currentLesson.value?.videoUrlKz || null,
        videoUrlRu: currentLesson.value?.videoUrlRu || null,
        videoUrlEn: currentLesson.value?.videoUrlEn || null,
        mediaErrorCode: code
      }
    }).catch(() => {})
  }

  const backToCategories = () => {
    selectedLessonCategory.value = null
    currentLessons.value = []
    currentLesson.value = null
  }

  const backToModeSelection = async () => {
    showLessons.value = false
    selectedMode.value = 'TRAINING_MODE'
    selectedLessonCategory.value = null
    currentLessons.value = []
    currentLesson.value = null
    lessonVideoSrc.value = null

    if (typeof onReturnToModeSelection === 'function') {
      await onReturnToModeSelection()
    }
  }

  const startOnlineLessons = async () => {
    selectedMode.value = 'ONLINE_LESSONS'
    showLessons.value = true
    await loadLessonCategories()
  }

  watch(getCurrentVideoUrl, (src) => {
    lessonVideoSrc.value = src || null
  }, { immediate: true })

  return {
    showLessons,
    lessonCategories,
    selectedLessonCategory,
    currentLessons,
    currentLesson,
    lessonsLoading,
    lessonVideoPlayer,
    lessonVideoSrc,
    resolvedLessonVideoUrl,
    loadLessonCategories,
    selectLessonCategory,
    selectLesson,
    onLessonVideoError,
    backToCategories,
    backToModeSelection,
    startOnlineLessons
  }
}
